// Pure aggregation helpers for PLSS (Public Land Survey System) grouping.
// `aggregateByTownshipRange` takes legacy UPPER_SNAKE deal rows (no page uses it since the Density page was removed);
// `aggregateBySection` takes joined TRACT rows (the deal Map tab).
// One bucket per TWP-RNG, per TWP-RNG-SEC, or per county.

import { normalizeCounty } from "@warwick-investment-group/wig-react-library";

import { isOpen } from "./dealMetrics";
import { canonicalTrs, tractLandKey } from "./aliquot";

const numOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Normalize a county string for joining deal rows to county GeoJSON
 * features. Trims whitespace, uppercases, and strips a trailing
 * " COUNTY" token so the various legacy formats ("McClain", "McClain
 * County", "MCCLAIN ", "mcclain county ") all collapse to "MCCLAIN".
 *
 * Used by aggregateByCounty when keying buckets and by callers (e.g.
 * the deals map page) when looking up a feature's bucket — both sides
 * MUST go through this same function or the join will silently miss.
 *
 * @param {*} s
 * @returns {string} Normalized county name (may be empty string).
 */
// The join key itself lives in wig-react-library (shared with the report map
// widgets); re-exported so both sides of the join keep one import.
export { normalizeCounty };

// Pick the top broker (by count) from an accumulating Map<string, number>.
const pickTopBroker = (brokerCounts) => {
  let topBroker = null;
  let topBrokerCount = 0;
  for (const [broker, count] of brokerCounts.entries()) {
    if (count > topBrokerCount) {
      topBroker = broker;
      topBrokerCount = count;
    }
  }
  return { topBroker, topBrokerCount };
};

/**
 * Aggregate rows by Township-Range (TWP|RNG). Returns a Map keyed by the
 * composite key `${TWP}|${RNG}` so callers can iterate deterministically.
 *
 * @param {Array} rows
 * @returns {Map<string, {
 *   key: string, twp: string, rng: string,
 *   deals: Array, count: number,
 *   netAcres: number, totalBonus: number,
 *   sections: Set<string>,
 *   topBroker: string|null, topBrokerCount: number
 * }>}
 */
export const aggregateByTownshipRange = (rows) => {
  const result = new Map();
  if (!Array.isArray(rows)) return result;

  // Accumulate per-bucket broker counts in a side map so we can resolve
  // `topBroker` in a single pass at the end.
  const brokerCountsByKey = new Map();

  for (const r of rows) {
    const twp = r?.TWP;
    const rng = r?.RNG;
    if (!twp || !rng) continue;
    const key = `${twp}|${rng}`;

    let bucket = result.get(key);
    if (!bucket) {
      bucket = {
        key,
        twp,
        rng,
        deals: [],
        count: 0,
        netAcres: 0,
        totalBonus: 0,
        sections: new Set(),
        topBroker: null,
        topBrokerCount: 0,
      };
      result.set(key, bucket);
      brokerCountsByKey.set(key, new Map());
    }

    bucket.deals.push(r);
    bucket.count += 1;

    const n = numOrNull(r?.NET_ACRES);
    if (n != null) bucket.netAcres += n;
    const b = numOrNull(r?.TOTAL_BONUS);
    if (b != null) bucket.totalBonus += b;

    if (r?.SEC) bucket.sections.add(String(r.SEC));

    const broker = r?.BROKER ?? "(none)";
    const bc = brokerCountsByKey.get(key);
    bc.set(broker, (bc.get(broker) || 0) + 1);
  }

  for (const [key, bucket] of result.entries()) {
    const { topBroker, topBrokerCount } = pickTopBroker(
      brokerCountsByKey.get(key) || new Map()
    );
    bucket.topBroker = topBroker;
    bucket.topBrokerCount = topBrokerCount;
  }

  return result;
};

// ---------------------------------------------------------------------------
// Tract-driven aggregation (deals map)
// ---------------------------------------------------------------------------
//
// The deals map is built from lease/deed TRACTS, because location lives on the
// tract, not the deal. Each input row is one tract joined to its deal (see
// `joinTractRows` in ./dealTracts):
//
//   tract fields:  state, county, township, range, section,
//                  co_net_acres_delivered
//   deal fields:   deal_id, deal_key, deal_status, total_bonus, broker
//
// Counting rules:
//   - `count` / `openCount` are DISTINCT deals in the bucket. A deal with
//     tracts in two sections counts once in each; two tracts of one deal in
//     the same section count once there.
//   - `netAcres` sums the tracts' `co_net_acres_delivered` in the bucket
//     (the column `deals.deal_net_acres_delivered` rolls up, de-hub
//     migration 081) — counting each piece of LAND once. Competing deals
//     often have their own tract rows for the same aliquot; those rows share
//     a `tractLandKey` and contribute the largest of their acreages, not the
//     sum.
//   - `totalBonus` sums the deal-level `total_bonus` once per distinct deal
//     in the bucket. Bonus is not split across tracts, so a deal spanning two
//     sections contributes its full bonus to both.
//   - `topBroker` is by distinct-deal count.

const dealIdOf = (r) => r?.deal_id ?? r?.deal_key ?? null;

const newAccumulator = () => ({
  acresByLand: new Map(), // tractLandKey -> largest acreage seen
  dealIds: new Set(),
  openIds: new Set(),
  brokerDeals: new Map(), // broker -> Set(dealId)
});

// Fold one tract row into a bucket; returns false when the row has no deal.
const accumulate = (bucket, acc, r) => {
  const dealId = dealIdOf(r);
  if (dealId === null) return false;
  const n = numOrNull(r?.co_net_acres_delivered);
  if (n != null) {
    const land = tractLandKey(r);
    const prev = acc.acresByLand.get(land);
    if (prev === undefined || n > prev) {
      bucket.netAcres += n - (prev ?? 0);
      acc.acresByLand.set(land, n);
    }
  }
  if (!acc.dealIds.has(dealId)) {
    acc.dealIds.add(dealId);
    bucket.deals.push(r);
    bucket.count += 1;
    const b = numOrNull(r?.total_bonus);
    if (b != null) bucket.totalBonus += b;
    if (isOpen(r)) {
      acc.openIds.add(dealId);
      if ("openCount" in bucket) bucket.openCount += 1;
    }
    const broker = r?.broker ?? "(none)";
    if (!acc.brokerDeals.has(broker)) acc.brokerDeals.set(broker, new Set());
    acc.brokerDeals.get(broker).add(dealId);
  }
  return true;
};

const finishTopBroker = (bucket, acc) => {
  const counts = new Map();
  for (const [broker, ids] of acc.brokerDeals.entries()) counts.set(broker, ids.size);
  const { topBroker, topBrokerCount } = pickTopBroker(counts);
  bucket.topBroker = topBroker;
  bucket.topBrokerCount = topBrokerCount;
};

/**
 * Aggregate joined tract rows by section. Keyed by `canonicalTrs` ("7N-7W-21")
 * so tract spellings ("07N") and survey-grid spellings ("007N") meet.
 *
 * @param {Array} rows  joined tract rows (see block comment above)
 * @returns {Map<string, {
 *   key: string, twp: string, rng: string, sec: string,
 *   deals: Array, count: number, openCount: number, netAcres: number,
 *   totalBonus: number, topBroker: string|null, topBrokerCount: number
 * }>}  `deals` holds the first tract row of each distinct deal
 */
export const aggregateBySection = (rows) => {
  const result = new Map();
  if (!Array.isArray(rows)) return result;
  const accs = new Map();

  for (const r of rows) {
    const key = canonicalTrs(r?.township, r?.range, r?.section);
    if (!key) continue;
    let bucket = result.get(key);
    if (!bucket) {
      const [twp, rng, sec] = key.split("-");
      bucket = {
        key,
        twp,
        rng,
        sec,
        deals: [],
        count: 0,
        openCount: 0,
        netAcres: 0,
        totalBonus: 0,
        topBroker: null,
        topBrokerCount: 0,
      };
      accs.set(key, newAccumulator());
    }
    if (accumulate(bucket, accs.get(key), r) && !result.has(key)) {
      result.set(key, bucket);
    }
  }

  for (const [key, bucket] of result.entries()) finishTopBroker(bucket, accs.get(key));
  return result;
};

// Both row shapes: the map's joined tract rows (lower-case `state` /
// `county`) and the legacy UPPER_SNAKE deal rows (kept; still covered by tests).
const stateOf = (r) => r?.state ?? r?.STATE;
const countyOf = (r) => r?.county ?? r?.COUNTY;

/** Distinct, sorted state values present in `rows`. */
export const distinctStates = (rows) =>
  [...new Set((Array.isArray(rows) ? rows : []).map(stateOf).filter(Boolean))].sort();

/**
 * Distinct, sorted county values in `rows`, optionally filtered to a
 * specific state. Pass `null`/`undefined`/`""` to get counties across
 * all states.
 */
export const distinctCounties = (rows, state) =>
  [
    ...new Set(
      (Array.isArray(rows) ? rows : [])
        .filter((r) => !state || stateOf(r) === state)
        .map(countyOf)
        .filter(Boolean)
    ),
  ].sort();
